import { CartProduct, CartState } from "@/types/cart";
import { calculateDealerPrice } from "./helpers";
import {
  PaymentAmountProps,
  PaymentState,
  selectedCouponProp,
} from "@/types/payment";
import { AppDispatch } from "@/store/store";
import {
  applyCoupon,
  applyCredit,
  applyWallet,
  createOrder,
  postPaymentSummary,
  updatePaymentData,
  updatePaymentStatusByAdmin,
} from "@/store/slices/paymentSlice";
import cashfree from "./Cashfree";
const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;
const API_BASE_PAYMENT_URL = process.env.NEXT_PUBLIC_API_BASE_PAYMENT_URL;

export const calculateBulkOrderTotalAmountWithOutGST = (
  cart: CartState | null
): number => {
  if (!cart || !Array.isArray(cart?.manufacturers)) return 0;

  let totalAmount = 0;

  cart.manufacturers.forEach((manufacturer) => {
    manufacturer.products.forEach((product) => {
      // Ensure Offer_price and NDP_Price are strings and handle undefined cases
      const ndpPrice = parseFloat(
        (product?.buy_quantity &&
        product?.Type === "sales" &&
        product?.Sales_Qunatity !== null &&
        product?.Sales_Qunatity !== 0 &&
        product?.Sales_Price !== null &&
        Number(product?.Sales_Price) !== 0
          ? product?.Sales_Price
          : product.NDP_Price
        )?.toString() || "0"
      );

      // Use Offer_price only if it's greater than 0, otherwise use NDP_Price
      const price = ndpPrice;

      totalAmount += price * (product.buy_quantity || 0);
    });
  });

  return Math.floor(totalAmount);
};

export const calculateBulkOrderTotalAmountWithOutGSTAfterSlab_Discount = (
  cart: CartState | null
): number => {
  if (!cart || !Array.isArray(cart?.manufacturers)) return 0;

  const slab_discount = calculateManufacturerDiscount(cart);

  const totalAmount = calculateBulkOrderTotalAmountWithOutGST(cart);

  return Math.floor(totalAmount - slab_discount);
};

export const calculateBulkOrderTotalAmountWithOutGSTAfter_Coupon = (
  cart: CartState | null,
  selectedCoupon: selectedCouponProp,
  useCreditAmount: number,
  useWalletAmount: number,
  creditOpen: boolean,
  walletOpen: boolean
): number => {
  if (!cart || !Array.isArray(cart?.manufacturers)) return 0;

  const counpon_discount = selectedCoupon?.couponDiscountTaken;

  const cart_amount_after_Slab_discount =
    calculateBulkOrderTotalAmountWithOutGSTAfterSlab_Discount(cart);

  const adv_discount = calculateThreePercent(
    cart_amount_after_Slab_discount - useCreditAmount - useWalletAmount,
    selectedCoupon?.couponDiscountTaken,
    creditOpen,
    walletOpen
  );

  if (selectedCoupon?.couponDiscountTaken === 0) {
    return Math.floor(cart_amount_after_Slab_discount - adv_discount);
  }

  return Math.floor(cart_amount_after_Slab_discount - counpon_discount);
};

export const calculateDifferencePercentage = (
  upperAmount: number,
  lowerAmount: number
): number => {
  if (upperAmount === 0) {
    return 0;
  }

  const difference = upperAmount - lowerAmount;
  const percentageDifference = (difference / upperAmount) * 100;

  return percentageDifference;
};

export const calculateTotalBulkOrderUpdatedGST = (
  cart: CartState | null,
  selectedCoupon: selectedCouponProp = {
    code: "",
    couponDiscountPercentage: "0",
    couponDiscountTaken: 0,
  },
  useCreditAmount: number,
  useWalletAmount: number,
  creditOpen: boolean,
  walletOpen: boolean
): number => {
  if (!cart || !Array.isArray(cart.manufacturers)) return 0;

  const originalGST = calculateTotalBulkOrderGST(cart);
  const originalCartValue = calculateBulkOrderTotalAmountWithOutGST(cart);
  const updatedCartValue = calculateBulkOrderTotalAmountWithOutGSTAfter_Coupon(
    cart,
    selectedCoupon,
    useCreditAmount,
    useWalletAmount,
    creditOpen,
    walletOpen
  );

  const differencePercentage = calculateDifferencePercentage(
    originalCartValue,
    updatedCartValue
  );

  const adjustedGST = originalGST * (1 - differencePercentage / 100);

  return Math.max(adjustedGST, 0);
};

export const calculateBulkOrderValueAmountToPay = (
  cart: CartState | null,
  selectedCoupon: selectedCouponProp,
  useWalletAmount: number,
  useCreditAmount: number
): {
  totalAmountWithGST: number;
  totalAmountWithoutGST: number;
  couponDiscountAmount: number;
  adjustedGST: number;
  walletAmountUsed: number;
  creditAmountUsed: number;
} => {
  if (!cart || !Array.isArray(cart?.manufacturers))
    return {
      totalAmountWithGST: 0,
      totalAmountWithoutGST: 0,
      couponDiscountAmount: 0,
      adjustedGST: 0,
      walletAmountUsed: 0,
      creditAmountUsed: 0,
    };

  const cartValue = calculateBulkOrderTotalAmountWithOutGST(cart);
  const gstValue = calculateTotalBulkOrderGST(cart);
  const slabDiscount = calculateManufacturerDiscount(cart);
  let discountedCartValue = cartValue - slabDiscount;

  const couponDiscountAmount =
    (discountedCartValue *
      Number(selectedCoupon?.couponDiscountPercentage || 0)) /
    100;

  discountedCartValue -= couponDiscountAmount;

  // const shippingCost = discountedCartValue >= 3000 ? 0 : 250;

  const discountPercentage =
    ((cartValue - discountedCartValue) / cartValue) * 100;
  const adjustedGST = gstValue * (1 - discountPercentage / 100);

  let totalDiscountedCartValueWithGST = discountedCartValue + adjustedGST;

  totalDiscountedCartValueWithGST = Math.max(
    totalDiscountedCartValueWithGST,
    0
  );

  const walletAmountUsed = Math.min(
    useWalletAmount,
    totalDiscountedCartValueWithGST
  );
  let amountAfterWallet = totalDiscountedCartValueWithGST - walletAmountUsed;

  const creditAmountUsed = Math.min(useCreditAmount, amountAfterWallet);
  amountAfterWallet -= creditAmountUsed;

  return {
    totalAmountWithGST: Math.round(Math.max(amountAfterWallet, 0)),
    totalAmountWithoutGST: discountedCartValue,
    couponDiscountAmount,
    adjustedGST,
    walletAmountUsed,
    creditAmountUsed,
  };
};

export const calculateTotalBulkQuantity = (cart?: CartState | null): number => {
  if (!cart || !Array.isArray(cart.manufacturers)) return 0;

  return cart?.manufacturers.reduce((total, manufacturer) => {
    return (
      total +
      manufacturer.products.reduce(
        (sum, product) => sum + product?.buy_quantity,
        0
      )
    );
  }, 0);
};

export const calculateBulkbikesShippingCost = (
  cart?: CartState | null
): number => {
  return calculateTotalBulkQuantity(cart) * 550;
};

export const calculateBulkbikesTotalAmount = (
  cart?: CartState | null
): number => {
  if (!cart || !Array.isArray(cart.manufacturers)) return 0;

  let totalAmount = 0;

  cart.manufacturers.forEach((manufacturer) => {
    if (manufacturer.manufacture_name === "GARRARI BIKES") {
      manufacturer.products.forEach((product) => {
        const mrpPrice = parseFloat(product.MRP_price?.toString() || "0");
        const ndpPrice = parseFloat(
          (product?.buy_quantity &&
          product?.Type === "sales" &&
          product?.Sales_Qunatity !== null &&
          product?.Sales_Qunatity !== 0 &&
          product?.Sales_Price !== null &&
          Number(product?.Sales_Price) !== 0
            ? product?.Sales_Price
            : product.NDP_Price
          )?.toString() || "0"
        );
        const gst = product.GST ?? 0;
        const buyQuantity = product.buy_quantity ?? 0;

        const finalPrice = calculateDealerPrice(mrpPrice, ndpPrice, gst);

        totalAmount += finalPrice * buyQuantity;
      });
    }
  });

  return Math.floor(totalAmount);
};

export const calculateBulkNormalShippingCost = (
  cart?: CartState | null
): number => {
  if (!cart || !Array.isArray(cart.manufacturers)) return 0;

  let totalAmount = 0;

  cart.manufacturers.forEach((manufacturer) => {
    manufacturer.products.forEach((product) => {
      const skuId = product.SKU_ID || ""; // Ensure SKU_ID is a string
      if (!skuId.startsWith("D")) {
        const mrpPrice = parseFloat(product.MRP_price?.toString() || "0");
        const ndpPrice = parseFloat(
          (product?.buy_quantity &&
          product?.Type === "sales" &&
          product?.Sales_Qunatity !== null &&
          product?.Sales_Qunatity !== 0 &&
          product?.Sales_Price !== null &&
          Number(product?.Sales_Price) !== 0
            ? product?.Sales_Price
            : product.NDP_Price
          )?.toString() || "0"
        );
        const gst = product.GST ?? 0;
        const buyQuantity = product.buy_quantity ?? 0;

        const finalPrice = calculateDealerPrice(mrpPrice, ndpPrice, gst);

        totalAmount += finalPrice * buyQuantity;
      }
    });
  });

  return totalAmount >= 3000 ? 0 : totalAmount === 0 ? 0 : 250;
};

export const calculateTotalBulkOrderGST = (cart?: CartState | null): number => {
  if (!cart || !Array.isArray(cart.manufacturers)) return 0;

  let totalGST = 0;

  cart.manufacturers.forEach((manufacturer) => {
    manufacturer.products.forEach((product) => {
      const ndpPrice = parseFloat(
        (product?.buy_quantity &&
        product?.Type === "sales" &&
        product?.Sales_Qunatity !== null &&
        product?.Sales_Qunatity !== 0 &&
        product?.Sales_Price !== null &&
        Number(product?.Sales_Price) !== 0
          ? product?.Sales_Price
          : product.NDP_Price
        )?.toString() || "0"
      );
      const gst = product.GST ?? 0;
      const buyQuantity = product.buy_quantity ?? 0;

      const price = ndpPrice;
      totalGST += calculateGST(price, gst) * buyQuantity;
    });
  });

  return totalGST;
};

export const calculateGST = (price: number, gstPercentage: number): number => {
  // Ensure both price and gstPercentage are valid numbers
  if (isNaN(price) || isNaN(gstPercentage)) {
    throw new Error("Invalid input. Price and GST percentage must be numbers.");
  }

  // Calculate GST amount
  return (price * gstPercentage) / 100;
};

export const totalBulkCartAmountToPay = (cart: CartState | null): number => {
  if (!cart || !Array.isArray(cart.manufacturers)) return 0;

  let totalAmount = 0;

  cart.manufacturers.forEach((manufacturer) => {
    manufacturer.products?.forEach((product) => {
      const mrpPrice = parseFloat(product.MRP_price?.toString() || "0");
      const ndpPrice = parseFloat(
        (product?.buy_quantity &&
        product?.Type === "sales" &&
        product?.Sales_Qunatity !== null &&
        product?.Sales_Qunatity !== 0 &&
        product?.Sales_Price !== null &&
        Number(product?.Sales_Price) !== 0
          ? product?.Sales_Price
          : product.NDP_Price
        )?.toString() || "0"
      );
      const gst = product.GST ?? 0;
      const buyQuantity = product.buy_quantity ?? 0;

      const price = calculateDealerPrice(mrpPrice, ndpPrice, gst);

      totalAmount += price * buyQuantity;
    });
  });

  return Math.floor(totalAmount); // Ensuring proper rounding
};

export const applyCreditAmount = (
  phoneNumber: string | null,
  cart: CartState | null,
  selectedCoupon: selectedCouponProp,
  useWalletAmount: number,
  useCreditAmount: number,
  dispatch: AppDispatch,
  updatePaymentData: (data: Partial<PaymentState>) => {
    type: string;
    payload: Partial<PaymentState>;
  },
  creditOpen: boolean,
  walletOpen: boolean
): number => {
  const totalAmount =
    calculateBulkOrderTotalAmountWithOutGSTAfterSlab_Discount(cart);
  const updated_gst = calculateTotalBulkOrderUpdatedGST(
    cart,
    selectedCoupon,
    useCreditAmount,
    useWalletAmount,
    creditOpen,
    walletOpen
  );

  let amount = totalAmount + updated_gst;

  // console.log(amount, useCreditAmount, useWalletAmount);

  // Apply credit first
  if (useCreditAmount > amount) {
    const appliedCredit = amount;
    dispatch(updatePaymentData({ useCreditAmount: appliedCredit }));
    dispatch(
      postPaymentSummary({
        PhoneNumber: phoneNumber,
        Credit: appliedCredit,
        Coupon: selectedCoupon?.couponDiscountTaken || 0,
        Wallet: useWalletAmount || 0,
      })
    );
    amount = 0; // Credit covered full amount
  } else {
    amount -= useCreditAmount;
  }

  // console.log(amount, useCreditAmount, useWalletAmount);

  // Apply wallet next
  if (useWalletAmount > amount) {
    const appliedWallet = amount;
    dispatch(updatePaymentData({ useWalletAmount: appliedWallet }));
    dispatch(
      postPaymentSummary({
        PhoneNumber: phoneNumber,
        Credit: useCreditAmount || 0,
        Coupon: selectedCoupon?.couponDiscountTaken || 0,
        Wallet: appliedWallet,
      })
    );
    amount = 0;
  } else {
    amount -= useWalletAmount;
  }

  // console.log(amount, useCreditAmount, useWalletAmount);

  return Math.max(amount, 0);
};

export const calculateBulkOrderAmountPay = (cart: CartState | null): number => {
  if (!cart) return 0;
  const totalAmount_after_coupon =
    calculateBulkOrderTotalAmountWithOutGSTAfterSlab_Discount(cart);

  return Math.round(Math.max(totalAmount_after_coupon, 0));
};

export const calculateThreePercent = (
  amount: number,
  couponDiscount: number,
  creditOpen: boolean,
  walletOpen: boolean
): number => {
  if (creditOpen) {
    return 0;
  } else if (walletOpen) {
    return 0;
  } else if (couponDiscount === 0) {
    return Math.max((amount * 3) / 100, 0);
  }
  return 0;
};

export const calculateBulkOrderAmountToPay = (
  phoneNumber: string | null,
  cart: CartState | null,
  selectedCoupon: selectedCouponProp,
  useWalletAmount: number,
  useCreditAmount: number,
  dispatch: AppDispatch,
  updatePaymentData: (data: Partial<PaymentState>) => {
    type: string;
    payload: Partial<PaymentState>;
  },
  creditOpen: boolean,
  walletOpen: boolean
): number => {
  if (!cart) return 0;

  const amount_before_three_percent_discount =
    calculateBulkOrderAmountPay(cart);

  // console.log(
  //   "amount_before_three_percent_discount",
  //   amount_before_three_percent_discount
  // );

  applyCreditAmount(
    phoneNumber,
    cart,
    selectedCoupon,
    useWalletAmount,
    useCreditAmount,
    dispatch,
    updatePaymentData,
    creditOpen,
    walletOpen
  );

  const threePercentDiscount = calculateThreePercent(
    amount_before_three_percent_discount - useCreditAmount - useWalletAmount,
    selectedCoupon?.couponDiscountTaken,
    creditOpen,
    walletOpen
  );

  // console.log("threePercentDiscount", threePercentDiscount);
  const updated_gst = calculateTotalBulkOrderUpdatedGST(
    cart,
    selectedCoupon,
    useCreditAmount,
    useWalletAmount,
    creditOpen,
    walletOpen
  );
  // console.log("updated_gst", updated_gst);
  // const shippingCost =
  //   amount_before_three_percent_discount + updated_gst >= 3000 ? 0 : 250;

  const after_shipping_cost =
    amount_before_three_percent_discount + updated_gst;

  // Ensure final amount to pay is not negative
  const finalAmount =
    after_shipping_cost -
    threePercentDiscount -
    useCreditAmount -
    useWalletAmount -
    selectedCoupon?.couponDiscountTaken;

  const roundedAmount = Math.round(Math.max(finalAmount, 0));

  // console.log("Final Amount To Pay (before check):", roundedAmount);

  return roundedAmount < 5 ? 0 : roundedAmount;
};

export const getFormattedDueDate = (orderDate: string): string => {
  const dueDate = new Date(orderDate); // Ensure orderDate is parsed correctly
  dueDate.setDate(dueDate.getDate() + 45); // Add 45 days to the order date

  // Get the day, month, and year for formatting
  const day = String(dueDate.getDate()).padStart(2, "0");
  const year = dueDate.getFullYear();

  // Array of month names for formatting
  const monthNames: string[] = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const month = monthNames[dueDate.getMonth()]; // Get the month name

  // Return the formatted due date string
  return `${day} ${month}, ${year}`;
};

// Function to get the day suffix
export const getDaySuffix = (day: number): string => {
  if (day >= 11 && day <= 13) {
    return "th";
  }
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

// Function to format a date after adding 30 days
export const formatDateAfter30Days = (date: string): string => {
  const updatedDate = new Date(date);
  updatedDate.setDate(updatedDate.getDate() + 30); // Add 30 days

  const months: string[] = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const day: number = updatedDate.getDate();
  const month: string = months[updatedDate.getMonth()];

  return `${month} ${day}${getDaySuffix(day)}`;
};

export const formatDate = (updateDate: string | Date): string => {
  if (!updateDate) return ""; // Handle potential undefined/null cases
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  return new Date(updateDate).toLocaleDateString("en-US", options);
};

// Function to calculate due date (days left from today)
export const calculateDueDate = (orderDate: string | Date): number => {
  const currentDate: Date = new Date(); // Get today's date
  const orderDateObj: Date = new Date(orderDate); // Parse the order date
  const dueDate: Date = new Date(orderDateObj);
  dueDate.setDate(orderDateObj.getDate() + 45); // Add 45 days

  const timeDiff: number = dueDate.getTime() - currentDate.getTime();
  return Math.ceil(timeDiff / (1000 * 3600 * 24)); // Days left
};

// Function to add a given number of days to a date
export const addDays = (date: string | Date, days: number): Date => {
  const result: Date = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const getCreditCategory = (
  creditLimit: number | undefined
): "Silver" | "Gold" | "Diamond" | "" => {
  if (creditLimit && creditLimit < 30000) return "Silver";
  if (creditLimit && creditLimit >= 30000 && creditLimit <= 70000)
    return "Gold";
  return "Diamond";
};

export const calculateSpentPercentage = (
  totalAmount: number,
  spentAmount: string | number
): number => {
  if (!totalAmount) return 0; // Avoid division by zero

  const spent =
    typeof spentAmount === "string" ? parseFloat(spentAmount) : spentAmount;
  if (isNaN(spent)) return 0; // Handle invalid string inputs

  return Math.floor((spent / totalAmount) * 100); // Using Math.floor for consistency
};

export const handleGeneratePaymentURL = async (
  bulkUserID: string | null,
  setPaymentSessionId: (id: string) => void,
  setTransactionId: (id: string) => void,
  amountToPay: number
): Promise<void> => {
  if (!bulkUserID) {
    console.error("User ID is required");
    return;
  }

  try {
    const response = await fetch(
      `${API_BASE_PAYMENT_URL}/${bulkUserID}/${
        amountToPay > 0 ? amountToPay : 1
      }`
    );

    if (!response.ok) {
      throw new Error(`API request failed with status: ${response.status}`);
    }

    const data: PaymentAmountProps = await response.json();

    setPaymentSessionId(data.Payment_Session_ID);
    setTransactionId(data.LinkID);
  } catch (error) {
    console.error("An error occurred during the API request:", error);
  }
};

export const handleBulkOrder = async (
  phoneNumber: string | null,
  cart: CartState | null,
  dispatch: AppDispatch,
  transactionId: string | null,
  bulkUserID: string | null,
  selectedAddress: number | null,
  useWalletAmount: number,
  useCreditAmount: number,
  selectedCoupon: selectedCouponProp | null,
  setOrderNo: (id: string | null) => void,
  setShowOrderConfirmation: (id: boolean) => void,
  adminPlacedOrder: boolean,
  creditOpen: boolean,
  walletOpen: boolean
): Promise<void> => {
  if (!adminPlacedOrder) return;

  try {
    // console.log("Enter handleBulkOrder UseEffect order Create");
    await handleBulkOrderCreate(
      phoneNumber,
      cart,
      dispatch,
      transactionId,
      bulkUserID,
      selectedAddress,
      useWalletAmount,
      useCreditAmount,
      selectedCoupon,
      setOrderNo,
      creditOpen,
      walletOpen
    );
    setShowOrderConfirmation(true);
  } catch (error) {
    console.error("Error while creating order:", error);
  }
};

export const handleBulkOrderCreate = (
  phoneNumber: string | null,
  cart: CartState | null,
  dispatch: AppDispatch,
  transactionId: string | null,
  bulkUserID: string | null,
  selectedAddress: number | null,
  useWalletAmount: number,
  useCreditAmount: number,
  selectedCoupon: selectedCouponProp | null,
  setOrderNo: (id: string | null) => void,
  creditOpen: boolean,
  walletOpen: boolean
): void => {
  if (!cart || !Array?.isArray(cart?.manufacturers)) return;

  const slab_discount = calculateManufacturerDiscount(cart) || 0;

  const mainProducts: CartProduct[] = [];

  cart?.manufacturers?.forEach((manufacturer) => {
    manufacturer?.products?.forEach((product) => {
      mainProducts?.push({
        ...product,
      });
    });
  });

  // Construct items arrays for dealer and main products
  const mapCartToItems = (products: CartProduct[]) =>
    products.map((product) => ({
      SKU_ID: product?.SKU_ID,
      quantity: product?.buy_quantity,
      MRP_price: Number(product?.MRP_price),
      NDP_price:
        calculateDealerPrice(
          parseInt(product?.MRP_price),
          parseInt(
            product?.buy_quantity &&
              product?.Type === "sales" &&
              product?.Sales_Qunatity !== null &&
              product?.Sales_Qunatity !== 0 &&
              product?.Sales_Price !== null &&
              Number(product?.Sales_Price) !== 0
              ? product?.Sales_Price
              : product.NDP_Price
          ),
          product?.GST
        ) * product?.buy_quantity,
    }));

  const mainItems = mapCartToItems(mainProducts);

  // Helper function to process shared logic
  const processSharedLogic = (response: any) => {
    const orderNumber = response?.payload?.OrderNumber;
    setOrderNo(orderNumber);

    // console.log(response);

    if (useCreditAmount > 0 && orderNumber) {
      const creditData = {
        UserID: Number(bulkUserID),
        order_no: orderNumber,
        amount: useCreditAmount,
      };
      // console.log(creditData);
      dispatch(applyCredit(creditData));
    }

    if (selectedCoupon && selectedCoupon?.code?.length > 0) {
      applyBulkCoupon(orderNumber, bulkUserID, dispatch, selectedCoupon);
    }

    if (useWalletAmount && useWalletAmount > 0 && orderNumber) {
      // console.log("useWalletAmount", useWalletAmount);
      debitFromBulkWallet(orderNumber, dispatch, bulkUserID, useWalletAmount);
    }
  };

  // Create order for main products
  const createMainOrder = () => {
    const mainOrderData = {
      merchantTransactionId: transactionId,
      shippingID: selectedAddress,
      Shipping_Amount: 0,
      Slab_discount: slab_discount,
      Coupon_discount: selectedCoupon?.couponDiscountTaken || 0,
      Sub_total: calculateBulkOrderTotalAmountWithOutGSTAfter_Coupon(
        cart,
        selectedCoupon || {
          code: "",
          couponDiscountPercentage: "0",
          couponDiscountTaken: 0,
        },
        useCreditAmount,
        useWalletAmount,
        creditOpen,
        walletOpen
      ),
      GST: calculateTotalBulkOrderUpdatedGST(
        cart,
        selectedCoupon || {
          code: "",
          couponDiscountPercentage: "0",
          couponDiscountTaken: 0,
        },
        useCreditAmount,
        useWalletAmount,
        creditOpen,
        walletOpen
      ),
      Wallet: useWalletAmount,
      Credit: useCreditAmount,
      Three_percent_discount:
        calculateBulkOrderAmountToPay(
          phoneNumber,
          cart,
          selectedCoupon || {
            code: "",
            couponDiscountPercentage: "0",
            couponDiscountTaken: 0,
          },
          useWalletAmount,
          useCreditAmount,
          dispatch,
          updatePaymentData,
          creditOpen,
          walletOpen
        ) > 0 &&
        calculateThreePercent(
          calculateBulkOrderAmountPay(cart) - useCreditAmount - useWalletAmount,
          selectedCoupon?.couponDiscountTaken || 0,
          creditOpen,
          walletOpen
        ),
      items: mainItems,
    };
    // console.log(mainOrderData);
    return dispatch(createOrder(mainOrderData));
  };

  // Handle both orders
  const handleBothOrders = async () => {
    try {
      if (mainItems?.length > 0) {
        const mainResponse = await createMainOrder();
        processSharedLogic(mainResponse);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  handleBothOrders();
};

export const applyBulkCoupon = (
  orderID: string,
  bulkUserID: string | null,
  dispatch: AppDispatch,
  selectedCoupon: selectedCouponProp | null
): void => {
  const couponData = {
    userID: bulkUserID,
    couponCode: selectedCoupon?.code || "",
    discountAmount: selectedCoupon?.couponDiscountTaken || 0,
    orderNo: orderID,
  };
  dispatch(applyCoupon(couponData));
};

export const debitFromBulkWallet = (
  orderNumber: string,
  dispatch: AppDispatch,
  bulkUserID: string | null,
  useWalletAmount: number
): void => {
  const walletData = {
    userID: bulkUserID,
    amount: useWalletAmount,
    transactionType: "debit",
    orderNumber: orderNumber,
  };
  dispatch(applyWallet(walletData));
};

// Cashfree Payment set
export const initiateBulkPayment = async (
  phoneNumber: string | null,
  bulkUserID: string | null,
  dispatch: AppDispatch,
  transactionId: string | null,
  setOpenAdminPlacedDialog: (id: boolean) => void,
  paymentSessionId: string | null,
  cart: CartState | null,
  selectedAddress: number | null,
  useWalletAmount: number,
  useCreditAmount: number,
  selectedCoupon: selectedCouponProp | null,
  setOrderNo: (id: string | null) => void,
  setShowOrderConfirmation: (id: boolean) => void,
  creditOpen: boolean,
  walletOpen: boolean
): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, 4000));

  const amountToPay = calculateBulkOrderAmountToPay(
    phoneNumber,
    cart,
    selectedCoupon || {
      code: "",
      couponDiscountPercentage: "0",
      couponDiscountTaken: 0,
    },
    useWalletAmount,
    useCreditAmount,
    dispatch,
    updatePaymentData,
    creditOpen,
    walletOpen
  );

  // 🟢 Case: Free or Admin-triggered payment
  if (amountToPay === 0) {
    dispatch(updatePaymentStatusByAdmin(transactionId)).then(async () => {
      setOpenAdminPlacedDialog(true);
    });
    return;
  }

  // 🧩 Prepare checkout
  const isMobile = window.innerWidth <= 768;

  const checkoutOptions = {
    paymentSessionId,
    redirectTarget: document.getElementById("cashfree-payment"),
    container: "_blank",
    appearance: {
      width: isMobile ? "100vw" : "900px",
      height: isMobile ? "100vh" : "600px",
    },
    openInNewTab: true,
  };

  // ✅ Flag to prevent duplicate order creation
  let hasOrderCreated = false;

  if (paymentSessionId) {
    // ✅ Start Cashfree Checkout immediately (don’t wait)
    cashfree?.checkout(checkoutOptions).then((result: any) => {
      console.log("Cashfree checkout initiated:", result);
    });

    const startTime = Date.now();

    const pollPaymentStatus = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/status/payment/${transactionId}`
        );
        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();

        const paymentStatuses = data?.paymentStatuses || [];
        const isSuccess = paymentStatuses.some(
          (s: string) => s?.toUpperCase() === "SUCCESS"
        );

        if (isSuccess && !hasOrderCreated) {
          hasOrderCreated = true;
          clearInterval(intervalId);

          console.log("✅ Payment successful — creating bulk order...");

          await handleBulkOrderCreate(
            phoneNumber,
            cart,
            dispatch,
            transactionId,
            bulkUserID,
            selectedAddress,
            useWalletAmount,
            useCreditAmount,
            selectedCoupon,
            setOrderNo,
            creditOpen,
            walletOpen
          );

          setShowOrderConfirmation(true);
          console.log("✅ Bulk order created successfully");
        } else if (!isSuccess) {
          console.log("Waiting for payment success...");
        }

        // ⏱ Stop polling after 5 minutes
        if (Date.now() - startTime > 300000) {
          clearInterval(intervalId);
          console.warn("⏹ Stopped polling after 5 minutes");
          setShowOrderConfirmation(false);
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    };

    // ✅ Begin polling every 5 seconds
    const intervalId = setInterval(pollPaymentStatus, 5000);

    // 🧹 Cleanup on unload or component unmount
    window.addEventListener("beforeunload", () => clearInterval(intervalId));
  }
};

export const calculateManufacturerDiscount = (
  cart: CartState | null
): number => {
  if (!cart || !Array.isArray(cart.manufacturers)) return 0;

  return cart.manufacturers.reduce((totalDiscount, manufacturer) => {
    const { additional_discount_slab, discount, products } = manufacturer;

    // Calculate total price for the manufacturer
    const totalPrice = products.reduce(
      (total: number, product: CartProduct) => {
        const priceTotal = calculateDealerPrice(
          Number(product.MRP_price),
          Number(
            product?.buy_quantity &&
              product?.Type === "sales" &&
              product?.Sales_Qunatity !== null &&
              product?.Sales_Qunatity !== 0 &&
              product?.Sales_Price !== null &&
              Number(product?.Sales_Price) !== 0
              ? product?.Sales_Price
              : product.NDP_Price
          ),
          product.GST
        );
        return total + priceTotal * product.buy_quantity;
      },
      0
    );

    const totalPriceWithoutGST = products.reduce(
      (total: number, product: CartProduct) => {
        const priceTotal = Number(
          product?.buy_quantity &&
            product?.Type === "sales" &&
            product?.Sales_Qunatity !== null &&
            product?.Sales_Qunatity !== 0 &&
            product?.Sales_Price !== null &&
            Number(product?.Sales_Price) !== 0
            ? product?.Sales_Price
            : product.NDP_Price
        );
        return total + priceTotal * product.buy_quantity;
      },
      0
    );

    let discountAmount = 0;

    // Check if additional discount slab and discount are valid
    if (
      additional_discount_slab &&
      discount &&
      totalPrice >= additional_discount_slab
    ) {
      discountAmount = (totalPriceWithoutGST * parseFloat(discount)) / 100;
    }

    return totalDiscount + discountAmount;
  }, 0);
};

export const getUnqualifiedManufacturers = (cart: CartState | null) => {
  if (!cart || !Array.isArray(cart?.manufacturers)) {
    return [];
  }

  return cart.manufacturers
    .map((manufacturer) => {
      if (!manufacturer || !Array?.isArray(manufacturer?.products)) {
        return null;
      }

      const totalAmount = manufacturer?.products?.reduce((sum, product) => {
        if (
          !product ||
          isNaN(product?.buy_quantity) ||
          isNaN(
            parseFloat(
              product?.buy_quantity &&
                product?.Type === "sales" &&
                product?.Sales_Qunatity !== null &&
                product?.Sales_Qunatity !== 0 &&
                product?.Sales_Price !== null &&
                Number(product?.Sales_Price) !== 0
                ? product?.Sales_Price
                : product.NDP_Price
            )
          )
        ) {
          return sum;
        }
        return (
          sum +
          product?.buy_quantity *
            calculateDealerPrice(
              Number(product?.MRP_price),
              Number(
                product?.buy_quantity &&
                  product?.Type === "sales" &&
                  product?.Sales_Qunatity !== null &&
                  product?.Sales_Qunatity !== 0 &&
                  product?.Sales_Price !== null &&
                  Number(product?.Sales_Price) !== 0
                  ? product?.Sales_Price
                  : product.NDP_Price
              ),
              product?.GST
            )
        );
      }, 0);

      if (
        isNaN(manufacturer?.MOQ_slab) ||
        totalAmount >= manufacturer?.MOQ_slab
      ) {
        return null;
      }

      return {
        manufacture_name: manufacturer?.manufacture_name,
        manufacture_image: manufacturer?.manufacture_images,
        amount_needed: Math?.max(
          0,
          Math.floor(manufacturer?.MOQ_slab - totalAmount)
        ),
      };
    })
    .filter(Boolean);
};
